import React, { useRef, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import { readPortalSession, writePortalSession } from "../../lib/storage/portalSession";
import { createReturn, uploadAttachment } from "../../services/api/portal";
import { normalizeError } from "../../services/http/errors";

const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

type Resolution = "REFUND" | "STORE_CREDIT" | "EXCHANGE";

const OPTIONS: { key: Resolution; icon: React.ReactNode; title: string; desc: string; chip?: string }[] = [
  { key: "REFUND", icon: <CreditCardOutlinedIcon />, title: "Original Refund", desc: "Back to your bank account" },
  { key: "STORE_CREDIT", icon: <AccountBalanceWalletOutlinedIcon />, title: "Instant Store Credit", desc: "SAR 10 bonus for your next order", chip: "VALUE PICK" },
  { key: "EXCHANGE", icon: <InventoryOutlinedIcon />, title: "Easy Exchange", desc: "Swap for a different item" },
];

export default function PortalResolution() {
  const { portalSlug = "" } = useParams();
  const nav = useNavigate();
  const session = useMemo(() => readPortalSession(), []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [reason, setReason] = useState(session?.reason || "Doesn't fit");
  const [resolution, setResolution] = useState<Resolution>(session?.resolution || "REFUND");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [toast, setToast] = useState({ open: false, msg: "", severity: "error" as "error" | "warning" });

  if (!session || session.portalSlug !== portalSlug) return <Navigate to={`/r/${portalSlug}`} replace />;
  if (!session.verified) return <Navigate to={`/r/${portalSlug}/verify`} replace />;
  if (!session.selectedItemIds.length) return <Navigate to={`/r/${portalSlug}/items`} replace />;

  const addFiles = (incoming: FileList | null) => {
    if (!incoming) return;
    const next = [...files];
    for (let i = 0; i < incoming.length; i++) {
      if (next.length >= MAX_FILES) {
        setToast({ open: true, msg: `Maximum ${MAX_FILES} photos allowed`, severity: "warning" });
        break;
      }
      const f = incoming[i];
      if (f.size > MAX_FILE_SIZE) {
        setToast({ open: true, msg: `${f.name} exceeds 5 MB limit`, severity: "warning" });
        continue;
      }
      next.push(f);
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    setSubmitting(true);
    try {
      writePortalSession({ ...session, reason, resolution });
      const resolutionMap: Record<string, string> = { REFUND: "refund", STORE_CREDIT: "store_credit", EXCHANGE: "exchange" };
      const result = await createReturn(portalSlug, {
        requested_resolution: resolutionMap[resolution] || "refund",
        notes_customer: reason !== "Doesn't fit" ? reason : undefined,
        is_warranty: false,
        customer_email: session.email || undefined,
        items: session.selectedItemIds.map((id) => ({
          order_item_id_external: id,
          sku: id,
          quantity: 1,
          reason_code: reason.toLowerCase().replace(/\s+/g, "_"),
          reason_note: reason,
        })),
      });

      // Upload photos if any
      if (files.length > 0 && result.return_number) {
        setUploadStatus("Uploading photos...");
        let failed = 0;
        for (const f of files) {
          try {
            await uploadAttachment(result.return_number, f);
          } catch {
            failed++;
          }
        }
        if (failed > 0) {
          setToast({ open: true, msg: `${failed} photo(s) failed to upload`, severity: "warning" });
        }
      }

      nav(`/r/${portalSlug}/success`);
    } catch (e) {
      const err = normalizeError(e);
      setToast({ open: true, msg: err.message, severity: "error" });
    } finally {
      setSubmitting(false);
      setUploadStatus("");
    }
  };

  return (
    <>
      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={() => setToast((p) => ({ ...p, open: false }))} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>

      <Card sx={{ maxWidth: 520, width: "100%" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={3}>
            <IconButton onClick={() => nav(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">Resolution</Typography>
          </Stack>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Return Reason</InputLabel>
            <Select value={reason} label="Return Reason" onChange={(e) => setReason(e.target.value)}>
              <MenuItem value="Doesn't fit">Doesn't fit</MenuItem>
              <MenuItem value="Damaged">Damaged</MenuItem>
              <MenuItem value="Not as described">Not as described</MenuItem>
              <MenuItem value="Changed mind">Changed mind</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
            Preferred Solution
          </Typography>

          <Stack spacing={1.5} mb={3}>
            {OPTIONS.map((opt) => (
              <Card
                key={opt.key}
                variant="outlined"
                sx={{
                  cursor: "pointer",
                  borderColor: resolution === opt.key ? "primary.main" : "divider",
                  borderWidth: resolution === opt.key ? 2 : 1,
                  transition: "all 0.15s",
                  "&:hover": { borderColor: "primary.light" },
                }}
                onClick={() => setResolution(opt.key)}
              >
                <Stack direction="row" alignItems="center" sx={{ p: 2 }}>
                  <Box sx={{ color: resolution === opt.key ? "primary.main" : "text.secondary", mr: 2, display: "flex" }}>
                    {opt.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {opt.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {opt.desc}
                    </Typography>
                  </Box>
                  {opt.chip && <Chip label={opt.chip} size="small" color="success" variant="outlined" />}
                </Stack>
              </Card>
            ))}
          </Stack>

          {/* Photo upload */}
          <Box sx={{ mb: 3 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              hidden
              onChange={(e) => addFiles(e.target.files)}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddPhotoAlternateOutlinedIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={files.length >= MAX_FILES}
            >
              Add photos{files.length > 0 ? ` (${files.length}/${MAX_FILES})` : " (optional)"}
            </Button>
            {files.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                {files.map((f, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: "relative",
                      width: 64,
                      height: 64,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box
                      component="img"
                      src={URL.createObjectURL(f)}
                      alt={f.name}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeFile(i)}
                      sx={{
                        position: "absolute",
                        top: -4,
                        right: -4,
                        bgcolor: "background.paper",
                        boxShadow: 1,
                        p: 0.25,
                        "&:hover": { bgcolor: "error.light" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Button variant="contained" size="large" fullWidth onClick={submit} disabled={submitting}>
            {submitting ? (uploadStatus || "Submitting...") : "Complete Request"}
          </Button>
        </CardContent>
      </Card>
    </>
  );
}
